import React, { useState, useEffect } from 'react'
import Seo from '../components/Seo'
import ContactForm from '../components/ContactForm'
import { getOptimizedImageUrl } from '../lib/image'
import { getCmsPage, getCompanyInfo } from '../lib/cms'

export default function ContactPage() {
  const [pageData, setPageData] = useState(null)
  const [companyInfo, setCompanyInfo] = useState(null)

  useEffect(() => {
    async function loadData() {
      try {
        const [page, comp] = await Promise.all([
          getCmsPage('kontakt'),
          getCompanyInfo(),
        ])
        if (page) setPageData(page)
        if (comp) setCompanyInfo(comp)
      } catch (err) {
        console.error('Error loading ContactPage CMS data:', err)
      }
    }
    loadData()
  }, [])

  const heroTitle = pageData?.hero_title || 'Kontakt'
  const heroDescription = pageData?.hero_description || 'Velkommen til Lyn Entreprenør Aps - din pålidelige partner inden for maskiner og udstyr til byggeri på Fyn.'

  const companyName = companyInfo?.company_name || 'Lyn Entreprenør Aps'
  const cvr = companyInfo?.cvr || '46264126'
  const address = companyInfo?.address || 'Krogsbjerg 1 (krogsberg 1)'
  const postalAndCity = companyInfo?.postal_and_city || '5683 Haarby, Danmark'
  const phone = companyInfo?.phone || '+45 55236932'
  const phoneClean = phone.replace(/[^0-9+]/g, '')
  const email1 = companyInfo?.email_primary || 'jc@lyn-ent.dk'
  const email2 = companyInfo?.email_secondary || 'Info@Lyn-Ent.dk'
  const facebook = companyInfo?.facebook_url || 'https://www.facebook.com/LynEntrepreneor'
  return (
    <>
      <Seo
        title="Kontakt :: Lyn Entreprenør"
        description="Velkommen til Lyn Entreprenør Aps - din pålidelige partner inden for maskiner og udstyr til byggeri på Fyn."
        ogImage="https://assets.auxodev.dk/assets/a2a4e36d-8b70-41e2-a1fb-0085c6465cf9/img-2863-4_a568be4c.jpeg"
        canonicalUrl="https://www.lyn-ent.dk/kontakt/"
      />

      {/* Page Header */}
      <section className="bg-red-600 py-16 sm:py-24 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative text-left">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tight">
            {heroTitle}
          </h1>

          <p className="mt-4 text-lg sm:text-xl text-red-100 max-w-3xl leading-relaxed">
            {heroDescription}
          </p>
        </div>

        {/* V-shaped divider at the bottom */}
        <div className="absolute bottom-0 left-0 right-0 w-full overflow-hidden leading-none z-20 pointer-events-none">
          <svg
            className="relative block w-full h-8 sm:h-12 md:h-16 lg:h-20 text-white"
            viewBox="0 0 1200 120"
            preserveAspectRatio="none"
            fill="currentColor"
            aria-hidden="true"
          >
            <path d="M0,0 L600,120 L1200,0 L1200,120 L0,120 Z" />
          </svg>
        </div>
      </section>

      {/* Main Contact Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            {/* Left Contact Information */}
            <div className="lg:col-span-5 space-y-6 text-left">
              {/* Company Info Box */}
              <div className="space-y-5">
                <div className="pb-4">
                  <h3 className="text-xl font-bold text-slate-900">
                    {companyName}
                  </h3>
                  <p className="text-xs text-slate-500 font-mono mt-0.5">CVR: {cvr}</p>
                </div>

                <div className="space-y-4 text-sm">
                  {/* Address */}
                  <div className="flex items-start gap-3">
                    <div className="flex h-6 w-6 shrink-0 items-center justify-center text-red-600 text-base">
                      <i className="fa-solid fa-location-dot" aria-hidden="true" />
                    </div>
                    <div>
                      <strong className="block text-slate-900 font-medium">Adresse</strong>
                      <p className="text-slate-600">
                        {address}<br />
                        {postalAndCity}
                      </p>
                    </div>
                  </div>

                  {/* Phone */}
                  <div className="flex items-center gap-3">
                    <div className="flex h-6 w-6 shrink-0 items-center justify-center text-red-600 text-base">
                      <i className="fa-solid fa-phone" aria-hidden="true" />
                    </div>
                    <div>
                      <strong className="block text-slate-900 font-medium">Telefon</strong>
                      <a
                        href={`tel:${phoneClean}`}
                        className="text-base font-bold text-red-600 hover:text-red-700 transition whitespace-nowrap"
                      >
                        {phone}
                      </a>
                    </div>
                  </div>

                  {/* Email */}
                  <div className="flex items-center gap-3">
                    <div className="flex h-6 w-6 shrink-0 items-center justify-center text-red-600 text-base">
                      <i className="fa-solid fa-envelope" aria-hidden="true" />
                    </div>
                    <div>
                      <strong className="block text-slate-900 font-medium">Email</strong>
                      <div className="flex flex-col">
                        <a
                          href={`mailto:${email1}`}
                          className="text-slate-700 hover:text-red-600 font-medium transition whitespace-nowrap"
                        >
                          {email1}
                        </a>
                        {email2 && (
                          <a
                            href={`mailto:${email2}`}
                            className="text-slate-700 hover:text-red-600 transition whitespace-nowrap text-xs text-slate-500"
                          >
                            {email2}
                          </a>
                        )}
                      </div>
                    </div>
                  </div>

                {/* Facebook */}
                  <div className="flex items-center gap-3">
                    <div className="flex h-6 w-6 shrink-0 items-center justify-center text-red-600 text-base">
                      <i className="fa-brands fa-facebook" aria-hidden="true" />
                    </div>
                    <div>
                      <strong className="block text-slate-900 font-medium">Facebook</strong>
                      <a
                        href={facebook}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-slate-700 hover:text-red-600 transition truncate block max-w-[220px]"
                      >
                        {facebook.replace('https://', '')}
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Map Element */}
              <div className="rounded-2xl overflow-hidden shadow-md border border-slate-200 bg-white">
                <div className="p-4 bg-white border-b border-slate-200 flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                    <i className="fa-solid fa-map-location-dot text-red-600" aria-hidden="true" />
                    <span>Krogsbjerg 1, 5683 Haarby, Danmark</span>
                  </div>
                  <a
                    href="https://maps.google.com/?q=Krogsbjerg+1,+5683+Haarby"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs font-bold text-red-600 hover:underline flex items-center gap-1"
                  >
                    <span>Åbn i Google Maps</span>
                    <i className="fa-solid fa-arrow-up-right-from-square text-[10px]" aria-hidden="true" />
                  </a>
                </div>
                <iframe
                  title="Kort over Lyn Entreprenør ApS beliggenhed i Haarby"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d18200!2d10.12!3d55.22!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x464ce7dbceca5847%3A0xb30438cf3ff8129!2sHaarby!5e0!3m2!1sda!2sdk!4v1680000000000!5m2!1sda!2sdk"
                  className="w-full h-80 sm:h-96 border-0"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>

            {/* Right Contact Form */}
            <div className="lg:col-span-7">
              <ContactForm />
            </div>
          </div>
        </div>
      </section>
    </>
  )
}
